import { DocumentGroup, caseNotesApi, documentsApi } from "@/api";
import { HANDLE_ERROR_LOCALLY_HEADER, withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import {
  fetchClientDocumentBlob,
  fetchClientDocumentPreviewBlob,
} from "@/common/components/layout/components/dropzone/hooks/dropzoneFetch.utils";
import { useDropzoneSection } from "@/common/components/layout/components/dropzone/hooks/useDropzoneSection";
import { useDropzoneUploadMutation } from "@/common/components/layout/components/dropzone/hooks/useDropzoneUploadMutation";
import { getLocalTimeZoneId } from "@/common/utils/getLocalTimeZoneId";
import { QueryKey } from "@/query/Query";

const MUTATION_KEY = ["client-document-upload"];

export function useClientDocuments(clientId: string | undefined) {
  const { onDrop, uploadingFiles, formatDialog } = useDropzoneUploadMutation({
    mutationKey: [...MUTATION_KEY, clientId],
    uploadFn: async (file, convertToPdf) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      const formData = new FormData();
      formData.append("file", file);
      await documentsApi.uploadDocumentToClientDropZone(
        { clientId, convertToPdf },
        { body: formData, headers: { [HANDLE_ERROR_LOCALLY_HEADER]: "true" } }
      );
    },
    invalidationKeys: [[QueryKey.ClientDocuments, clientId], [QueryKey.DropZoneFiles]],
  });

  const section = useDropzoneSection({
    zone: DocumentGroup.Client,
    queryKey: [QueryKey.ClientDocuments, clientId],
    queryFn: async () => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return documentsApi.getDocumentsFromClientDropZone({ clientId });
    },
    deleteFn: (files) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return documentsApi.deleteDocuments({ clientId, ids: files.map((f) => f.documentId) });
    },
    fetchDownloadBlobFn: (file) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return fetchClientDocumentBlob(clientId, file.documentId);
    },
    fetchPreviewBlobFn: (file) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return fetchClientDocumentPreviewBlob(clientId, file.documentId);
    },
    renameFn: (file, newFileName) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return documentsApi.renameDocument({
        clientId,
        documentId: file.documentId,
        renameDocumentRequest: { newFileName },
      });
    },
    createCaseNoteFn: (file) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return caseNotesApi.createCaseNoteFromDocument({
        clientId,
        createCaseNoteFromDocumentRequest: {
          source: DocumentGroup.Client,
          fileId: file.documentId,
          originTimeZoneId: getLocalTimeZoneId(),
        },
      });
    },
    convertToPdfFn: (file) => {
      if (!clientId) {
        throw new Error("Client ID is required");
      }
      return documentsApi.convertDocumentToPdf({ clientId, documentId: file.documentId }, withLocalErrorHandling);
    },
    invalidationKeys: [[QueryKey.ClientDocuments, clientId]],
    caseNoteInvalidationKeys: [[QueryKey.ClientCaseNotes, clientId]],
    onDrop,
    uploadingFiles,
    enabled: !!clientId,
  });

  return { ...section, formatDialog };
}
